/**
 * 3D hex placement helpers — flat-top axial coords → world XZ.
 *
 * The replay-parser's `hexToPixel` returns 2D pixel coords for the
 * Pixi viewer. Three.js wants world-space XYZ with Y up. This module
 * is the bridge — a single, tested mapping that all renderers in the
 * package share.
 */

import type { Hex } from "@pilot-protocol/replay-parser";

/** World-space hex tile size (center → corner). 1 unit = 1m in our scene. */
export const HEX_SIZE = 1;

/** Vertical offset between adjacent rows for flat-top hexes. */
export const ROW_HEIGHT = Math.sqrt(3) * HEX_SIZE;

/** Tile thickness for the hex floor mesh (also the elevation step). */
export const TILE_THICKNESS = 0.15;

export interface World3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Axial hex → world XZ at Y=0. Flat-top convention matches
 * `replay-parser/hex.ts::hexToPixel`. Z mirrors the parser's Y so the
 * top-down 2D layout maps to the top-down 3D view.
 */
export function hexToWorld(h: Hex, elevation = 0): World3 {
  return {
    x: HEX_SIZE * ((3 / 2) * h.q) + 0,
    y: elevation * TILE_THICKNESS + 0,
    z: HEX_SIZE * ((Math.sqrt(3) / 2) * h.q + Math.sqrt(3) * h.r) + 0,
  };
}

/**
 * Convert a 0..5 facing index to a Y-axis rotation in radians.
 *
 * Facing values: 0=N, 1=NE, 2=SE, 3=S, 4=SW, 5=NW. These mirror the
 * engine's `Facing` enum (see `server/mecharena/hex.py`); the engine
 * uses `DIRECTIONS[f]` to step in axial coords.
 *
 * The yaw maps `Facing.f` → the world-space direction vector that
 * `DIRECTIONS[f]` produces via `hexToWorld`:
 *
 *   N  → -Z   (yaw = π)
 *   NE → +X / -Z (yaw = 2π/3)
 *   SE → +X / +Z (yaw = π/3)
 *   S  → +Z   (yaw = 0)
 *   SW → -X / +Z (yaw = -π/3)
 *   NW → -X / -Z (yaw = -2π/3)
 *
 * Voxel parts are authored with the chest grille at +Z, so a mech
 * rendered at yaw=0 visually faces +Z (south). Facing N then sits the
 * mech with its chest at -Z, etc.
 *
 * The +0 coercion keeps facing=3 (S, yaw=0) from returning -0.
 */
export function facingToYaw(facing: number): number {
  return Math.PI - ((facing % 6) * Math.PI) / 3 + 0;
}

/**
 * Snake-cube-like radius walker — same shape as
 * `replay-parser/hex.ts::hexesInRadius`, kept local so this module
 * stays Three-only and doesn't add a parser import for callers that
 * already paid for it.
 */
export function ringInRadius(radius: number): Array<{ q: number; r: number }> {
  const out: Array<{ q: number; r: number }> = [];
  for (let q = -radius; q <= radius; q++) {
    const rLo = Math.max(-radius, -q - radius);
    const rHi = Math.min(radius, -q + radius);
    for (let r = rLo; r <= rHi; r++) {
      // Coerce -0 → +0 for the origin case so callers get a stable
      // structural-equality comparison.
      out.push({ q: q + 0, r: r + 0 });
    }
  }
  return out;
}
